import React from 'react'
import { forwardRef } from '@grupoboticario/flora-react-system'
import { FloraInputProps } from './types'
import {
  StyledInput,
  MessagesWrapper,
  StyledInputWrapper,
  InputLeftIcon,
  InputRightIcon,
  InputButtonWrapper,
  InputButton,
} from './styles'
import { FloraHelperText } from '@grupoboticario/flora-react-helper-text'
import { Label, FocusWrapper } from './components'
import { useFloraInput } from './useFloraInput'

/**
 * @component
 * @name FloraInput
 * @description O componente FloraInput é um elemento de formulário que permite ao usuário inserir um texto de linha única. Além desta funcionalidade nativa, também é possível utilizá-lo com máscaras, ícones, botão, mensagens auxiliares e uma variedade ampla de status.
 * @tutorial https://florabook.gb.tech/v2/index.html?path=/docs/core-components-florainput
 */
export const FloraInput = forwardRef<'input', FloraInputProps>(
  (
    {
      'autotagging-flora': autotaggingFlora = 'off',
      'data-flora-text': dataFloraText,
      'data-autotagging-flora-section': dataAutotaggingFloraSection = '',
      'aria-label': ariaLabel,
      'aria-disabled': ariaDisabled,
      children,
      id,
      helperText,
      required,
      onChange,
      onKeyDown,
      onBlur,
      onMouseDownCapture,
      status = 'neutral',
      label,
      labelPosition = 'internal',
      backgroundStyle = 'default',
      placeholder,
      surfaceColor = 'light',
      disabled,
      readOnly,
      leftIcon,
      rightIcon,
      mask,
      defaultValue,
      value,
      tooltipText,
      tabIndex,
      buttonProps,
      ...props
    },
    forwardedRef,
  ) => {
    const {
      charCount,
      ref,
      handleOnChange,
      handleOnKeyDown,
      handleOnMouseDownCaptureInput,
      handleOnMouseUpCaptureLabel,
      valueMasked,
      defaultValueMasked,
      clicked,
      handleOnBlur,
    } = useFloraInput({
      forwardedRef,
      onChange,
      onKeyDown,
      onBlur,
      onMouseDownCapture,
      mask,
      defaultValue,
      value,
    })

    return (
      <>
        <FocusWrapper
          labelPosition={labelPosition}
          hasValue={charCount > 0}
          hasEffect={!readOnly && !ariaDisabled && status !== 'loading'}
          surfaceColor={surfaceColor}
          hasButton={!!buttonProps}
          status={status}
          clicked={clicked}
          disabled={disabled || !!ariaDisabled}
        >
          {label && (
            <Label
              htmlFor={id}
              labelPosition={labelPosition}
              surfaceColor={surfaceColor}
              placeholder={placeholder}
              required={required}
              tooltipText={tooltipText}
              status={status}
              hasLeftIcon={!!leftIcon}
              hasRightIcon={!!rightIcon}
              disabled={disabled || !!ariaDisabled}
              onMouseUpCapture={handleOnMouseUpCaptureLabel}
            >
              {label}
            </Label>
          )}

          <InputButtonWrapper hasButton={!!buttonProps}>
            <StyledInputWrapper>
              {leftIcon && (
                <InputLeftIcon surfaceColor={surfaceColor} data-testid="InputLeftIcon">
                  {leftIcon}
                </InputLeftIcon>
              )}
              <StyledInput
                {...props}
                id={id}
                as="input"
                data-flora="input"
                ref={ref}
                status={!!buttonProps ? 'neutral' : status}
                hasLeftIcon={!!leftIcon}
                hasRightIcon={!!rightIcon}
                hasButton={!!buttonProps}
                placeholder={placeholder}
                labelPosition={labelPosition}
                clicked={clicked}
                onChange={handleOnChange}
                onKeyDown={handleOnKeyDown}
                onMouseDownCapture={handleOnMouseDownCaptureInput}
                onBlur={handleOnBlur}
                disabled={disabled}
                required={required}
                surfaceColor={surfaceColor}
                backgroundStyle={backgroundStyle}
                readOnly={readOnly || !!ariaDisabled || status === 'loading'}
                autotagging-flora={autotaggingFlora}
                data-autotagging-flora-section={dataAutotaggingFloraSection}
                data-flora-text={dataFloraText || label || ariaLabel}
                aria-label={ariaLabel}
                aria-disabled={ariaDisabled}
                aria-describedby={`helpertext-${id}`}
                aria-invalid={status === 'error'}
                value={valueMasked}
                defaultValue={defaultValueMasked}
                tabIndex={status === 'loading' ? -1 : tabIndex}
              >
                {children}
              </StyledInput>
              {rightIcon && (
                <InputRightIcon surfaceColor={surfaceColor} data-testid="InputRightIcon">
                  {rightIcon}
                </InputRightIcon>
              )}
            </StyledInputWrapper>
            {buttonProps && (
              <InputButton
                {...buttonProps}
                className="flora--input-button"
                hierarchy="primary"
                styleSemantic="neutral"
                isFull={false}
                isLoading={status === 'loading'}
                surfaceColor={surfaceColor}
                size="large"
                disabled={disabled}
                aria-disabled={ariaDisabled}
                hasIcon={!!buttonProps.icon}
              />
            )}
          </InputButtonWrapper>
          <MessagesWrapper>
            {helperText && (
              <FloraHelperText
                id={`helpertext-${id}`}
                status={disabled || ariaDisabled ? 'disabled' : status}
                surfaceColor={surfaceColor}
              >
                {helperText}
              </FloraHelperText>
            )}
          </MessagesWrapper>
        </FocusWrapper>
      </>
    )
  },
)
